/**
 * TrustGate Content Security Policy (CSP)
 * 
 * Comprehensive CSP implementation for XSS prevention
 * and content security hardening
 */

export interface CSPDirective {
  name: string;
  values: string[];
}

export interface CSPConfig {
  directives: CSPDirective[];
  reportOnly?: boolean;
  reportURI?: string;
}

export class ContentSecurityPolicy {
  private static defaultDirectives: CSPDirective[] = [
    {
      name: 'default-src',
      values: ["'self'"]
    },
    {
      name: 'script-src',
      values: ["'self'", "'unsafe-inline'", "'unsafe-eval'"] // Temporarily allow inline scripts for React
    },
    {
      name: 'style-src',
      values: ["'self'", "'unsafe-inline'", 'https://fonts.googleapis.com']
    },
    {
      name: 'font-src',
      values: ["'self'", 'https://fonts.gstatic.com', 'data:']
    },
    {
      name: 'img-src',
      values: ["'self'", 'data:', 'https:', 'blob:']
    },
    {
      name: 'connect-src',
      values: ["'self'", 'https://api.trustgate.io', 'https://firebase.googleapis.com', 'https://firestore.googleapis.com']
    },
    {
      name: 'frame-src',
      values: ["'none'"]
    },
    {
      name: 'object-src',
      values: ["'none'"]
    },
    {
      name: 'base-uri',
      values: ["'self'"]
    },
    {
      name: 'form-action',
      values: ["'self'"]
    },
    {
      name: 'frame-ancestors',
      values: ["'none'"]
    },
    {
      name: 'upgrade-insecure-requests',
      values: []
    },
    {
      name: 'block-all-mixed-content',
      values: []
    }
  ];

  /**
   * Generate CSP header string
   */
  static generateHeader(config: Partial<CSPConfig> = {}): string {
    const directives = config.directives || this.defaultDirectives;
    const reportOnly = config.reportOnly || false;
    
    const policyString = directives
      .map(directive => `${directive.name} ${directive.values.join(' ')}`)
      .join('; ');
    
    return reportOnly ? 
      `Content-Security-Policy-Report-Only: ${policyString}` : 
      `Content-Security-Policy: ${policyString}`;
  }

  /**
   * Generate CSP meta tag for HTML
   */
  static generateMetaTag(config: Partial<CSPConfig> = {}): string {
    const header = this.generateHeader(config);
    const directive = header.split(':')[0];
    const content = header.split(':').slice(1).join(':').trim();
    
    return `<meta http-equiv="${directive}" content="${content}">`;
  }

  /**
   * Create strict CSP config for production
   */
  static createStrictConfig(): CSPConfig {
    return {
      directives: [
        {
          name: 'default-src',
          values: ["'self'"]
        },
        {
          name: 'script-src',
          values: ["'self'"] // Remove unsafe-inline and unsafe-eval for production
        },
        {
          name: 'style-src',
          values: ["'self'", 'https://fonts.googleapis.com']
        },
        {
          name: 'font-src',
          values: ["'self'", 'https://fonts.gstatic.com', 'data:']
        },
        {
          name: 'img-src',
          values: ["'self'", 'data:', 'https:', 'blob:']
        },
        {
          name: 'connect-src',
          values: ["'self'", 'https://api.trustgate.io', 'https://firebase.googleapis.com', 'https://firestore.googleapis.com']
        },
        {
          name: 'frame-src',
          values: ["'none'"]
        },
        {
          name: 'object-src',
          values: ["'none'"]
        },
        {
          name: 'base-uri',
          values: ["'self'"]
        },
        {
          name: 'form-action',
          values: ["'self'"]
        },
        {
          name: 'frame-ancestors',
          values: ["'none'"]
        },
        {
          name: 'upgrade-insecure-requests',
          values: []
        },
        {
          name: 'block-all-mixed-content',
          values: []
        }
      ],
      reportOnly: false
    };
  }

  /**
   * Create development CSP config with more permissive settings
   */
  static createDevelopmentConfig(): CSPConfig {
    return {
      directives: [
        {
          name: 'default-src',
          values: ["'self'"]
        },
        {
          name: 'script-src',
          values: ["'self'", "'unsafe-inline'", "'unsafe-eval'", 'http://localhost:*', 'ws://localhost:*']
        },
        {
          name: 'style-src',
          values: ["'self'", "'unsafe-inline'", 'https://fonts.googleapis.com']
        },
        {
          name: 'font-src',
          values: ["'self'", 'https://fonts.gstatic.com', 'data:']
        },
        {
          name: 'img-src',
          values: ["'self'", 'data:', 'https:', 'blob:', 'http://localhost:*']
        },
        {
          name: 'connect-src',
          values: ["'self'", 'http://localhost:*', 'ws://localhost:*', 'https://api.trustgate.io', 'https://firebase.googleapis.com', 'https://firestore.googleapis.com']
        },
        {
          name: 'frame-src',
          values: ["'none'"]
        },
        {
          name: 'object-src',
          values: ["'none'"]
        },
        {
          name: 'base-uri',
          values: ["'self'"]
        },
        {
          name: 'form-action',
          values: ["'self'"]
        },
        {
          name: 'frame-ancestors',
          values: ["'none'"]
        }
      ],
      reportOnly: true // Use report-only mode in development
    };
  }

  /**
   * Apply CSP to document head
   */
  static applyToDocument(config: Partial<CSPConfig> = {}): void {
    if (typeof document === 'undefined') return;

    // Remove existing CSP meta tags
    const existingTags = document.querySelectorAll('meta[http-equiv*="Content-Security-Policy"]');
    existingTags.forEach(tag => tag.remove());

    // Add new CSP meta tag
    const metaTag = document.createElement('meta');
    const header = this.generateHeader(config);
    const [directive, content] = header.split(':');
    
    metaTag.setAttribute('http-equiv', directive.trim());
    metaTag.setAttribute('content', content.trim());
    
    document.head.appendChild(metaTag);
  }

  /**
   * Validate CSP configuration
   */
  static validateConfig(config: CSPConfig): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    // Check required directives
    const requiredDirectives = ['default-src', 'script-src', 'style-src'];
    const presentDirectives = config.directives.map(d => d.name);
    
    requiredDirectives.forEach(required => {
      if (!presentDirectives.includes(required)) {
        errors.push(`Missing required directive: ${required}`);
      }
    });

    // Check for unsafe practices in production
    if (!config.reportOnly) {
      const scriptDirective = config.directives.find(d => d.name === 'script-src');
      if (scriptDirective) {
        if (scriptDirective.values.includes("'unsafe-inline'")) {
          errors.push('Production CSP should not allow unsafe-inline scripts');
        }
        if (scriptDirective.values.includes("'unsafe-eval'")) {
          errors.push('Production CSP should not allow unsafe-eval');
        }
      }
    }

    // Check for wildcard usage
    config.directives.forEach(directive => {
      if (directive.values.includes('*')) {
        errors.push(`Wildcard (*) not recommended in directive: ${directive.name}`);
      }
    });

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Monitor CSP violations
   */
  static setupViolationMonitoring(callback?: (violation: SecurityPolicyViolationEvent) => void): void {
    if (typeof document === 'undefined') return;

    const handler = (event: SecurityPolicyViolationEvent) => {
      console.warn('CSP Violation:', {
        blockedURI: event.blockedURI,
        documentURI: event.documentURI,
        effectiveDirective: event.effectiveDirective,
        originalPolicy: event.originalPolicy,
        referrer: event.referrer,
        sourceFile: event.sourceFile,
        statusCode: event.statusCode,
        violatedDirective: event.violatedDirective
      });

      if (callback) {
        callback(event);
      }

      // Send to monitoring service in production
      if (process.env.NODE_ENV === 'production') {
        fetch('/api/security/csp-violation', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            blockedURI: event.blockedURI,
            documentURI: event.documentURI,
            effectiveDirective: event.effectiveDirective,
            originalPolicy: event.originalPolicy,
            sourceFile: event.sourceFile,
            statusCode: event.statusCode,
            violatedDirective: event.violatedDirective,
            timestamp: new Date().toISOString(),
            userAgent: navigator.userAgent
          })
        }).catch(() => {
          // Silently fail to avoid affecting user experience
        });
      }
    };

    document.addEventListener('securitypolicyviolation', handler);
  }

  /**
   * Generate nonce for inline scripts
   */
  static generateNonce(): string {
    const array = new Uint8Array(16);
    crypto.getRandomValues(array);
    return btoa(String.fromCharCode(...array));
  }

  /**
   * Create CSP with nonce support
   */
  static createNonceConfig(nonce: string): CSPConfig {
    return {
      directives: [
        {
          name: 'default-src',
          values: ["'self'"]
        },
        {
          name: 'script-src',
          values: ["'self `'${nonce}`"] // Use nonce instead of unsafe-inline
        },
        {
          name: 'style-src',
          values: ["'self `'${nonce}`", 'https://fonts.googleapis.com']
        },
        {
          name: 'font-src',
          values: ["'self'", 'https://fonts.gstatic.com', 'data:']
        },
        {
          name: 'img-src',
          values: ["'self'", 'data:', 'https:', 'blob:']
        },
        {
          name: 'connect-src',
          values: ["'self'", 'https://api.trustgate.io', 'https://firebase.googleapis.com', 'https://firestore.googleapis.com']
        },
        {
          name: 'frame-src',
          values: ["'none'"]
        },
        {
          name: 'object-src',
          values: ["'none'"]
        },
        {
          name: 'base-uri',
          values: ["'self'"]
        },
        {
          name: 'form-action',
          values: ["'self'"]
        },
        {
          name: 'frame-ancestors',
          values: ["'none'"]
        }
      ]
    };
  }
}

// React hook for CSP management
export const useContentSecurityPolicy = () => {
  const applyCSP = (config: Partial<CSPConfig> = {}) => {
    ContentSecurityPolicy.applyToDocument(config);
  };

  const generateNonce = () => {
    return ContentSecurityPolicy.generateNonce();
  };

  const createNonceConfig = (nonce: string) => {
    return ContentSecurityPolicy.createNonceConfig(nonce);
  };

  const validateConfig = (config: CSPConfig) => {
    return ContentSecurityPolicy.validateConfig(config);
  };

  return {
    applyCSP,
    generateNonce,
    createNonceConfig,
    validateConfig
  };
};

export default ContentSecurityPolicy;
